"use client"

import { useTranslations } from "next-intl"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Edit, Package, Search, X } from "lucide-react"
import { editListingEvents } from "@/lib/edit-listing-events"
import { createClient } from "@/utils/supabase/client"
import { formatPrice } from "@/lib/utils"
import { useMobile } from "@/hooks/use-mobile"
import { Skeleton } from "@/components/ui/skeleton"
import { SQUARE_LG, imagePresetUrl } from "@/lib/images/presets"
import { escapeIlike } from "@/lib/search/escape"
import type { ListingRow } from "@/types/listing"

const PAGE_SIZE = 32
const COLS = 4

type ListingFilterTab = "public" | "private"

type Listing = ListingRow

function parseImageUrls(images?: string[] | string): string[] {
  if (!images) return []
  if (Array.isArray(images)) return images.filter(Boolean)
  if (typeof images === "string") {
    try {
      const parsed = JSON.parse(images)
      return Array.isArray(parsed) ? parsed.filter(Boolean) : []
    } catch {
      return []
    }
  }
  return []
}

function ListingCellImage({ src, alt }: { src: string; alt: string }) {
  const [loaded, setLoaded] = useState(false)
  return (
    <div className="relative w-full h-full">
      {!loaded && <Skeleton className="absolute inset-0 rounded-2xl" aria-hidden />}
      <img
        // `grid-cols-4` `aspect-square` cell: ~83 CSS px on a phone, ~140 in
        // the desktop sheet -> up to ~280 device px. Rounded DOWN to SQUARE_LG
        // (256, a 9 % shortfall) rather than up to CARD_SM: CARD_SM is a
        // `contain` preset, so on a portrait source it would return 384x683 and
        // the square `object-cover` box would throw ~4x the pixels away.
        src={imagePresetUrl(src, SQUARE_LG)}
        alt={alt}
        loading="lazy"
        decoding="async"
        className={`w-full h-full object-cover transition-opacity duration-200 ${loaded ? "opacity-100" : "opacity-0"}`}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </div>
  )
}

function ListingGridCell({
  listing,
  isSelected,
  onToggle,
  onEdit,
}: {
  listing: Listing
  isSelected: (id: string) => boolean
  onToggle: (listing: Listing) => void
  onEdit: (listing: Listing) => void
}) {
  const selected = isSelected(listing.id)
  const imgs = parseImageUrls(listing.image_urls)
  const img = imgs[0]
  const priceStr = formatPrice(Number(listing.price ?? 0), listing.currency ?? "USD")
  return (
    <div className="relative aspect-square w-full">
      <button
        type="button"
        onClick={() => onToggle(listing)}
        className="relative z-0 isolate aspect-square rounded-2xl overflow-visible active:scale-95 transition-transform w-full"
      >
        <div className="absolute inset-0 rounded-2xl overflow-hidden">
          {img ? (
            <ListingCellImage src={img} alt={listing.title ?? ""} />
          ) : (
            <div className="w-full h-full bg-muted dark:bg-neutral-800 flex items-center justify-center min-h-0">
              <Package className="w-8 h-8 text-muted-foreground dark:text-neutral-500 shrink-0" />
            </div>
          )}
        </div>
        <div className="absolute z-10 top-1.5 left-1.5 bg-black/70 backdrop-blur-sm px-1.5 py-0.5 rounded-md">
          <p className="text-[10px] text-white font-semibold">{priceStr}</p>
        </div>
        <AnimatePresence>
          {selected && (
            <motion.div
              className="absolute z-20 inset-0 pointer-events-none rounded-2xl bg-white/20 ring-2 ring-inset ring-white"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ type: "tween", duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-white rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onEdit(listing) }}
        className="absolute bottom-1 right-1 z-30 w-7 h-7 rounded-lg bg-black/70 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/85 active:scale-95 transition-transform"
        aria-label="Edit listing"
      >
        <Edit className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
    </div>
  )
}

export interface ChatListingBottomSheetProps {
  currentUserId: string
  selectedIds: string[]
  onToggle: (listing: Listing) => void
  onClose: () => void
}

export function ChatListingBottomSheet({
  currentUserId,
  selectedIds,
  onToggle,
  onClose,
}: ChatListingBottomSheetProps) {
  const supabase = useMemo(() => createClient(), [])
  const [listings, setListings] = useState<Listing[]>([])
  const [offset, setOffset] = useState(0)
  const tChat = useTranslations("chat")
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [tab, setTab] = useState<ListingFilterTab>("public")
  const sentinelRef = useRef<HTMLDivElement>(null)
  const loadMoreArmedRef = useRef(true)
  const isMobile = useMobile()
  const hasSelection = selectedIds.length > 0
  const scrollMaxHeight = hasSelection && !isMobile
    ? "min(200px, max(120px, calc(100vh - 480px)))"
    : "256px"

  const fetchPage = useCallback(
    async (pageOffset: number, append: boolean) => {
      if (append) setLoadingMore(true)
      else setLoading(true)
      try {
        const isPrivate = tab === "private"
        // Phase 4 (2026-06-29) of shippo-listing-discriminator: visibility enum replaces is_chat_private
        const q = supabase
          .from("listings")
          .select("id, title, description, price, currency, image_urls, visibility")
          .eq("user_id", currentUserId)
          .is("deleted_at", null)
          .eq("visibility", isPrivate ? "chat_private" : "public")
          .order("created_at", { ascending: false })
          .range(pageOffset, pageOffset + PAGE_SIZE - 1)
        const { data, error } = await q
        if (error) throw error
        const rows = (data || []) as Listing[]
        setListings((prev) => (append ? [...prev, ...rows] : rows))
        setHasMore(rows.length === PAGE_SIZE)
        setOffset(pageOffset + rows.length)
      } catch {
        setListings((prev) => (append ? prev : []))
        setHasMore(false)
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [currentUserId, supabase, tab]
  )

  useEffect(() => {
    setOffset(0)
    setHasMore(true)
    loadMoreArmedRef.current = true
    fetchPage(0, false)
  }, [tab, currentUserId, fetchPage])

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore || searchQuery.trim()) return
    fetchPage(offset, true)
  }, [fetchPage, loadingMore, hasMore, offset, searchQuery])

  useEffect(() => {
    const node = sentinelRef.current
    if (!node || !hasMore || searchQuery.trim()) return
    const scrollRoot = node.closest("[data-chat-listing-scroll-root]") as Element | null
    if (!scrollRoot) return
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) return
        if (!entry.isIntersecting) {
          loadMoreArmedRef.current = true
          return
        }
        if (!loadMoreArmedRef.current) return
        loadMoreArmedRef.current = false
        loadMore()
      },
      { root: scrollRoot, rootMargin: "400px" }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, loadMore, searchQuery])

  // Re-arm after a page lands: IntersectionObserver only fires on a transition,
  // so a sentinel still in view after a short page would otherwise stall.
  useEffect(() => {
    loadMoreArmedRef.current = true
  }, [listings.length])

  const searchListings = useCallback(
    async (q: string) => {
      const trimmed = q.toLowerCase().trim()
      if (!trimmed) {
        setOffset(0)
        setHasMore(true)
        fetchPage(0, false)
        return
      }
      setLoading(true)
      try {
        const isPrivate = tab === "private"
        // Phase 4 (2026-06-29) of shippo-listing-discriminator: visibility enum replaces is_chat_private
        const { data, error } = await supabase
          .from("listings")
          .select("id, title, description, price, currency, image_urls, visibility")
          .eq("user_id", currentUserId)
          .is("deleted_at", null)
          .eq("visibility", isPrivate ? "chat_private" : "public")
          .or(`title.ilike.%${escapeIlike(trimmed)}%,description.ilike.%${escapeIlike(trimmed)}%`)
          .order("created_at", { ascending: false })
          .limit(PAGE_SIZE)
        if (error) throw error
        setListings((data || []) as Listing[])
        setHasMore(false)
        setOffset(0)
      } catch {
        setListings([])
      } finally {
        setLoading(false)
      }
    },
    [currentUserId, supabase, tab]
  )

  const debouncedSearch = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevSearchRef = useRef("")
  useEffect(() => {
    if (debouncedSearch.current) clearTimeout(debouncedSearch.current)
    if (searchQuery.trim()) {
      debouncedSearch.current = setTimeout(() => searchListings(searchQuery), 200)
    } else if (prevSearchRef.current.trim()) {
      setOffset(0)
      setHasMore(true)
      loadMoreArmedRef.current = true
      fetchPage(0, false)
    }
    prevSearchRef.current = searchQuery
    return () => {
      if (debouncedSearch.current) clearTimeout(debouncedSearch.current)
    }
  }, [searchQuery, searchListings, fetchPage])

  const filtered = listings
  const showLoadMoreSkeleton = loadingMore
  const isSelected = useCallback(
    (id: string) => selectedIds.includes(id),
    [selectedIds]
  )

  const containerStyle = !isMobile
    ? {
        maxHeight: hasSelection ? "min(320px, calc(100vh - 420px))" : "70vh",
        transition: "max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
      }
    : undefined

  const scrollTransition = "max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1)"

  return (
    <motion.div
      layout
      initial={false}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
      className="bg-card dark:bg-neutral-900 rounded-3xl mb-3 overflow-hidden animate-in slide-in-from-bottom-2 fade-in duration-200"
      style={containerStyle}
    >
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        <h3 className="text-[17px] font-semibold text-foreground dark:text-white">{tChat("selectListing")}</h3>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-muted active:bg-muted/80 transition-colors dark:bg-neutral-800 dark:active:bg-neutral-700"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-muted-foreground dark:text-neutral-400" strokeWidth={2.5} />
        </button>
      </div>

      <div className="flex mx-4 mb-3 p-1 bg-muted dark:bg-neutral-800 rounded-full">
        <button
          type="button"
          onClick={() => setTab("public")}
          className={`flex-1 py-2 text-[13px] font-semibold rounded-full transition-all duration-200 ${
            tab === "public" ? "bg-primary text-primary-foreground dark:bg-white dark:text-black" : "text-muted-foreground dark:text-neutral-400"
          }`}
        >
          {tChat("public")}
        </button>
        <button
          type="button"
          onClick={() => setTab("private")}
          className={`flex-1 py-2 text-[13px] font-semibold rounded-full transition-all duration-200 ${
            tab === "private" ? "bg-primary text-primary-foreground dark:bg-white dark:text-black" : "text-muted-foreground dark:text-neutral-400"
          }`}
        >
          {tChat("private")}
        </button>
      </div>

      <div className="mx-4 mb-3">
        <div className="flex items-center gap-2 bg-muted dark:bg-neutral-800 rounded-2xl px-3 py-2.5">
          <Search className="w-4 h-4 text-muted-foreground dark:text-neutral-500 shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={tChat("searchListings")}
            className="flex-1 bg-transparent text-base text-foreground placeholder:text-muted-foreground outline-none dark:text-white dark:placeholder:text-neutral-500"
          />
          {searchQuery ? (
            <button type="button" onClick={() => setSearchQuery("")} className="text-muted-foreground dark:text-neutral-500 p-0.5">
              <X className="w-4 h-4" />
            </button>
          ) : null}
        </div>
      </div>

      <motion.div
        layout
        initial={false}
        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
        data-chat-listing-scroll-root
        className="px-4 pb-4 overflow-y-auto scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none]"
        style={{ maxHeight: scrollMaxHeight, transition: scrollTransition }}
      >
        {loading ? (
          <div className="grid grid-cols-4 gap-2 py-4">
            {Array.from({ length: COLS * 3 }).map((_, i) => (
              <Skeleton key={i} className="aspect-square rounded-2xl bg-muted dark:bg-neutral-800" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-[15px] text-muted-foreground dark:text-neutral-500 py-8">{tChat("noListingsFound")}</p>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-2">
              {filtered.map((listing) => (
                <ListingGridCell
                  key={listing.id}
                  listing={listing}
                  isSelected={isSelected}
                  onToggle={onToggle}
                  onEdit={(l) => {
                  onClose()
                  editListingEvents.open({ listingId: l.id, listing: l, messageId: "", roomName: "" })
                }}
                />
              ))}
              {showLoadMoreSkeleton &&
                Array.from({ length: COLS }).map((_, i) => (
                  <Skeleton key={`more-${i}`} className="aspect-square rounded-2xl bg-muted dark:bg-neutral-800" />
                ))}
            </div>
            <div ref={sentinelRef} aria-hidden />
          </>
        )}
      </motion.div>
    </motion.div>
  )
}
